/**
 * UI primitives for the LLM Wiki web dashboard — thin, typed React
 * wrappers over the component classes in ./theme.css (dark ink + amber
 * design language; see web/README-UI.md for the full contract).
 *
 * Deliberately framework-light: no vendored @/components/ui/* imports,
 * no Radix/Base-UI — accessible by construction (focus rings from
 * theme.css, aria attributes, Escape/backdrop handling on Modal).
 */
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react"
import { Constellation } from "./Constellation"
import i18n from "../i18n"

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ")
}

/* ── Button ─────────────────────────────────────────────────────────── */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger"
export type ButtonSize = "sm" | "md" | "lg"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner and disables the button. */
  loading?: boolean
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: "btn-ghost",
  danger: "btn-danger",
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { variant = "secondary", size = "md", loading, className, children, disabled, type, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        className={cx(
          "btn",
          VARIANT_CLASS[variant],
          size === "sm" && "btn-sm",
          size === "lg" && "btn-lg",
          className,
        )}
        disabled={disabled || loading}
        {...rest}
      >
        {loading && <Spinner />}
        {children}
      </button>
    )
  },
)

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible name — required, icon-only buttons have no text. */
  label: string
  variant?: ButtonVariant
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ label, variant = "ghost", className, children, type, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        aria-label={label}
        title={label}
        className={cx("btn btn-icon", VARIANT_CLASS[variant], className)}
        {...rest}
      >
        {children}
      </button>
    )
  },
)

/* ── Card ───────────────────────────────────────────────────────────── */

export interface CardProps {
  title?: ReactNode
  description?: ReactNode
  /** Right-aligned header actions (buttons etc.). */
  actions?: ReactNode
  className?: string
  children?: ReactNode
}

export function Card({ title, description, actions, className, children }: CardProps) {
  const hasHeader = title !== undefined || actions !== undefined
  return (
    <section className={cx("card", className)}>
      {hasHeader && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: children !== undefined ? 16 : 0,
          }}
        >
          <div>
            {title !== undefined && <div className="card-title">{title}</div>}
            {description !== undefined && <div className="card-desc">{description}</div>}
          </div>
          {actions !== undefined && (
            <div style={{ display: "flex", gap: 8, flex: "none" }}>{actions}</div>
          )}
        </div>
      )}
      {children}
    </section>
  )
}

/* ── Form controls ──────────────────────────────────────────────────── */

export type InputProps = InputHTMLAttributes<HTMLInputElement>
export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx("input", className)} {...rest} />
  },
)

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx("textarea", className)} {...rest} />
  },
)

export type LabelProps = LabelHTMLAttributes<HTMLLabelElement>
export function Label({ className, ...rest }: LabelProps) {
  return <label className={cx("label", className)} {...rest} />
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Convenience options list; children win when provided. */
  options?: Array<{ value: string; label: string }>
}
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select({ className, options, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx("select", className)} {...rest}>
        {children ??
          options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
      </select>
    )
  },
)

/** Label + control + optional error, stacked with canonical spacing. */
export function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: ReactNode
  htmlFor?: string
  error?: string | null
  children: ReactNode
}) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error != null && error !== "" && <p className="field-error" role="alert">{error}</p>}
    </div>
  )
}

/* ── Badge / Spinner ────────────────────────────────────────────────── */

export type BadgeTone = "neutral" | "accent" | "danger" | "ok"

export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: BadgeTone
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cx(
        "badge",
        tone === "accent" && "badge-accent",
        tone === "danger" && "badge-danger",
        tone === "ok" && "badge-ok",
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Amber pulse dot for live/active states (ingest running, SSE connected). */
export function LiveDot({ label }: { label?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span className="pulse-dot" aria-hidden="true" />
      {label !== undefined && <span className="lw-muted" style={{ fontSize: 13 }}>{label}</span>}
    </span>
  )
}

export function Spinner({ size = "md", className }: { size?: "md" | "lg"; className?: string }) {
  return (
    <span
      role="status"
      aria-label={i18n.t("common.loading")}
      className={cx("spinner", size === "lg" && "spinner-lg", className)}
    />
  )
}

/* ── EmptyState ─────────────────────────────────────────────────────── */

export interface EmptyStateProps {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Primary invitation to act (a <Button>). */
  action?: ReactNode
  /** Ambient constellation backdrop (on by default — the signature). */
  constellation?: boolean
  className?: string
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  constellation = true,
  className,
}: EmptyStateProps) {
  return (
    <div className={cx("empty-state", className)}>
      {constellation && <Constellation intensity="subtle" opacity={0.7} />}
      {icon !== undefined && (
        <div style={{ color: "var(--lw-faint)", marginBottom: 4 }}>{icon}</div>
      )}
      <div className="empty-state-title">{title}</div>
      {description !== undefined && <p className="empty-state-desc">{description}</p>}
      {action !== undefined && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  )
}

/* ── Toasts ─────────────────────────────────────────────────────────── */

export type ToastTone = "neutral" | "success" | "error"
export interface ToastOptions {
  tone?: ToastTone
  /** Auto-dismiss delay ms (default 4500; errors 7000). */
  duration?: number
}

interface ToastItem {
  id: number
  message: ReactNode
  tone: ToastTone
}

interface ToastContextValue {
  toast: (message: ReactNode, options?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>")
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: ReactNode, options?: ToastOptions) => {
      const id = nextId.current++
      const tone = options?.tone ?? "neutral"
      setToasts((list) => [...list, { id, message, tone }])
      const duration = options?.duration ?? (tone === "error" ? 7000 : 4500)
      window.setTimeout(() => dismiss(id), duration)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-viewport" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.tone === "error" ? "alert" : "status"}
            className={cx(
              "toast",
              t.tone === "error" && "toast-error",
              t.tone === "success" && "toast-success",
            )}
          >
            <div style={{ flex: 1 }}>{t.message}</div>
            <button
              type="button"
              aria-label={i18n.t("common.dismiss_notification")}
              onClick={() => dismiss(t.id)}
              style={{
                background: "none",
                border: "none",
                color: "var(--lw-faint)",
                cursor: "pointer",
                fontSize: 15,
                lineHeight: 1,
                padding: 2,
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

/* ── Modal ──────────────────────────────────────────────────────────── */

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  /** Footer actions (typically a secondary Cancel + a primary action). */
  footer?: ReactNode
  children?: ReactNode
  /** Max width px override (default 460). */
  width?: number
}

export function Modal({ open, onClose, title, footer, children, width }: ModalProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  // Callers pass inline arrows for onClose; keep the latest in a ref so the
  // effect below depends only on `open` — re-running it on every parent
  // render would yank focus back to the first input on each keystroke.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current()
    }
    document.addEventListener("keydown", onKey)
    // Move focus into the dialog so keyboard users land inside it.
    const el = panelRef.current
    const focusable = el?.querySelector<HTMLElement>(
      "input, select, textarea, button, [tabindex]",
    )
    ;(focusable ?? el)?.focus()
    return () => document.removeEventListener("keydown", onKey)
  }, [open])

  if (!open) return null

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        className="modal"
        style={width !== undefined ? { maxWidth: width } : undefined}
        tabIndex={-1}
      >
        {title !== undefined && (
          <h3 id={titleId} style={{ marginBottom: 16 }}>
            {title}
          </h3>
        )}
        {children}
        {footer !== undefined && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 20,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Tooltip ────────────────────────────────────────────────────────── */

export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  return (
    <span className="tooltip-wrap">
      {children}
      <span role="tooltip" className="tooltip">
        {text}
      </span>
    </span>
  )
}
